import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ArrowUpRight, ArrowLeft, Twitter, Linkedin, Link as LinkIcon, Calendar, User } from "lucide-react";
import { posts, getPostBySlug } from "@/data/posts";
import { FadeIn, MaskedReveal } from "@/components/Reveal";
import { JsonLd, articleSchema, breadcrumbSchema } from "@/components/JsonLd";

export default function Insights() {
  return (
    <>
      <Helmet>
        <title>Security Insights & Field Notes | Quebec Security Services</title>
        <meta name="description" content="Deep-dive articles on Quebec security operations: mining, construction, retail, events, BSP licensing, Loi 25 compliance, and more." />
      </Helmet>

      <section className="px-6 md:px-10 pt-24 md:pt-32 pb-16 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-amber-500" />
          Insights · {posts.length}
        </div>
        <h1 className="font-display uppercase font-black text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] text-white max-w-5xl">
          <MaskedReveal>
            <span>Field notes.</span>
            <span className="text-amber-500">From the perimeter.</span>
          </MaskedReveal>
        </h1>
      </section>

      <section className="px-6 md:px-10 pb-24 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {posts.map((p, i) => (
            <FadeIn key={p.slug} delay={i * 0.05}>
              <Link
                to={`/insights/${p.slug}`}
                data-testid={`post-card-${p.slug}`}
                className="group block border border-white/10 hover:border-amber-500 transition-colors duration-300 overflow-hidden"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" style={{ transitionProperty: "transform" }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] bg-amber-500 text-black px-2.5 py-1">
                    {p.tag}
                  </div>
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                    <span>{new Date(p.published).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })}</span>
                    <span>·</span>
                    <span>{p.read}</span>
                  </div>
                  <h3 className="font-display uppercase font-black text-2xl md:text-3xl tracking-tight leading-tight text-white group-hover:text-amber-500 transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-zinc-400 text-sm md:text-base leading-relaxed">{p.excerpt}</p>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">By {p.author}</span>
                    <ArrowUpRight className="w-5 h-5 text-amber-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>
    </>
  );
}

export const InsightDetail = () => {
  const { slug } = useParams();
  const p = getPostBySlug(slug);
  if (!p) return <Navigate to="/insights" replace />;

  const related = posts.filter((x) => x.slug !== p.slug).slice(0, 3);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const shareTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(p.title)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  };
  const shareLinkedin = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank");
  };
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard");
  };

  return (
    <>
      <Helmet>
        <title>{`${p.title} | Quebec Security Services`}</title>
        <meta name="description" content={p.excerpt} />
        <meta name="keywords" content={p.keywords.join(", ")} />
        <meta property="og:title" content={p.title} />
        <meta property="og:description" content={p.excerpt} />
        <meta property="og:image" content={p.image} />
        <meta property="og:type" content="article" />
      </Helmet>
      <JsonLd data={articleSchema(p)} />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Insights", path: "/insights" },
        { name: p.title, path: `/insights/${p.slug}` },
      ])} />

      {/* Hero */}
      <section className="relative min-h-[70vh] w-full overflow-hidden bg-black flex items-end">
        <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="relative z-10 max-w-[1200px] mx-auto w-full px-6 md:px-10 py-20 md:py-24">
          <Link to="/insights" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-500 transition-colors mb-8">
            <ArrowLeft className="w-3 h-3" /> All insights
          </Link>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-8">
            <span className="w-8 h-px bg-amber-500" />
            {p.tag}
          </div>
          <h1 className="font-display uppercase font-black text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-white">
            <MaskedReveal>
              {p.title.split(" ").map((w, i) => <span key={i}>{w}&nbsp;</span>)}
            </MaskedReveal>
          </h1>
          <FadeIn delay={0.2}>
            <div className="mt-8 flex flex-wrap items-center gap-6 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
              <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-amber-500" /> {p.author}</div>
              <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-amber-500" /> {new Date(p.published).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}</div>
              <div>◉ {p.read}</div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Article body */}
      <section className="px-6 md:px-10 py-20 max-w-[1000px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-9">
            <FadeIn>
              <p className="text-xl md:text-2xl text-white leading-relaxed font-display tracking-tight">
                {p.excerpt}
              </p>
            </FadeIn>
            <div className="mt-12 space-y-6">
              {p.body.map((para, i) => (
                <FadeIn key={i} delay={i * 0.03}>
                  <p className="text-lg text-zinc-300 leading-relaxed">{para}</p>
                </FadeIn>
              ))}
            </div>

            <FadeIn delay={0.2}>
              <div className="mt-16 flex flex-wrap gap-2">
                {p.keywords.map((k) => (
                  <span key={k} className="border border-white/15 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] text-zinc-400">{k}</span>
                ))}
              </div>
            </FadeIn>
          </div>

          <aside className="md:col-span-3">
            <div className="md:sticky md:top-24">
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500 mb-4">→ Share</div>
              <div className="flex flex-col gap-2">
                <button data-testid="share-twitter" onClick={shareTwitter} className="group flex items-center justify-between border border-white/15 hover:border-amber-500 hover:text-amber-500 px-4 h-11 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors">
                  <span className="flex items-center gap-2"><Twitter className="w-3.5 h-3.5" /> Twitter</span>
                  <ArrowUpRight className="w-3 h-3" />
                </button>
                <button data-testid="share-linkedin" onClick={shareLinkedin} className="group flex items-center justify-between border border-white/15 hover:border-amber-500 hover:text-amber-500 px-4 h-11 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors">
                  <span className="flex items-center gap-2"><Linkedin className="w-3.5 h-3.5" /> LinkedIn</span>
                  <ArrowUpRight className="w-3 h-3" />
                </button>
                <button data-testid="share-copy" onClick={copyLink} className="group flex items-center justify-between border border-white/15 hover:border-amber-500 hover:text-amber-500 px-4 h-11 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors">
                  <span className="flex items-center gap-2"><LinkIcon className="w-3.5 h-3.5" /> Copy link</span>
                </button>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Related */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber-500 mb-6">→ Keep reading</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {related.map((r) => (
            <Link key={r.slug} to={`/insights/${r.slug}`} className="group border border-white/10 hover:border-amber-500 transition-colors">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={r.image} alt={r.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-2">{r.tag}</div>
                <h3 className="font-display uppercase font-black text-lg md:text-xl tracking-tight leading-tight text-white group-hover:text-amber-500 transition-colors">{r.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
};
